/**
 * Category Cockpit — screen 2: one category, one chapter at a time.
 *
 * Header (facts, reach of a change, the next action), a rail of 14 chapters with a status dot
 * each, the chapter's brief (what we know · what is missing · action) and its body. The aspects'
 * content comes from the existing category-characteristics route; the schema and the pending
 * gaps are loaded when their chapter opens. Below: the journal of this category.
 *
 * URL: /category-cockpit/category?path=<full path>&ch=<chapter>
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  dropCache, fetchAspects, fetchCategory, fetchContrast, fetchJournal, fetchSchema,
  type Aspect, type AspectsResponse, type CategoryDetail, type ChapterId, type ContrastDetail, type JournalEntry, type SchemaDetail, type Truncation,
} from '@/services/categoryCockpitService';
import { Shell, useCockpit, LAST_PATH_KEY } from './components/Shell';
import { AspectDots, Dots, ErrorBox, Ladder, Loading, StatePill, TierPill, Src } from './components/bits';
import { ChapterBody, chapterBrief } from './chapters';
import { useProvideAct } from './components/Act';
import { CHAPTERS, CH_TITLES, STATE_MAP, catUrl, cost, day, fmt, leaf, pct, plural, reachSentence, sitesOf, sum } from './lib/format';

function isAbort(err: unknown): boolean {
  return (err as { name?: string })?.name === 'AbortError';
}

function msgOf(err: unknown): string {
  return (err as { message?: string })?.message || String(err);
}

/**
 * A chapter's lazily-loaded payload, in three DISTINCT states — `undefined` while it is still
 * loading, `state: 'ok'` once it answered (its `data` may legitimately be null: the category
 * row does not exist, the sector has no schema), and `state: 'failed'` when the request failed.
 *
 * Collapsing the third into the second is what the dual review blocked on: a failed aspects
 * request rendered "the category row does not exist", and a failed journal request rendered
 * "no write recorded" — a transport error presented as a fact about the data, with no way to
 * retry short of reloading the page.
 */
/** how many journal rows the category panel shows before it offers the rest */
const JOURNAL_PAGE = 40;

type Settled<T> = { state: 'ok'; data: T } | { state: 'failed'; error: string };
/**
 * `undefined` = still loading. The discriminant is a STRING on purpose: this project compiles
 * with `strict: false`, and TypeScript does not narrow a boolean discriminant while
 * strictNullChecks is off, which type-checked away the very distinction this union exists to
 * make (verified against this tsconfig before the change).
 */
type Lazy<T> = Settled<T> | undefined;

/** the payload for the chapter body, which only ever sees loaded-or-loading (never a failure) */
function dataOf<T>(l: Lazy<T>): T | undefined {
  return l && l.state === 'ok' ? l.data : undefined;
}

function errorOf(l: Lazy<unknown>): string | null {
  if (!l || l.state === 'ok') return null;
  return l.error;
}

/** the failed load blocking this chapter, if any: what it was, and how to ask for it again */
function LoadFailed({ what, msg, retry }: { what: string; msg: string; retry: () => void }) {
  return (
    <div className="pmc-error">
      <div className="pmc-error-title">{what} could not be loaded</div>
      <div className="pmc-error-msg">{msg}</div>
      <button type="button" className="pmc-btn" onClick={retry}>Try again</button>
    </div>
  );
}

export default function CategoryCockpitBook() {
  const [params] = useSearchParams();
  const path = params.get('path') || '';
  return (
    /* ⛔ NO PROVIDER HERE, AND THAT IS THE FIX. It was hoisted above `<Shell>` in round 8 — which
       closed the site switch — and a route navigation unmounts this whole component anyway, so
       ANY position inside it is a position that can be unmounted. It lives in
       `CategoryCockpitLayout`, above the route boundary, and the entries live in a module store
       above even that. */
    <Shell active="cat" catLabel={path ? leaf(path) : null}>
      {path ? <BookBody path={path} /> : <div className="pmc-empty">Open a category from the <Link to="/category-cockpit">Categories</Link> screen.</div>}
    </Shell>
  );
}

function BookBody({ path }: { path: string }) {
  const { sectorId, siteId } = useCockpit();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const chParam = params.get('ch') as ChapterId | null;
  const ch: ChapterId = chParam && CHAPTERS.includes(chParam) ? chParam : 'aspects';

  const [d, setD] = useState<CategoryDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aspects, setAspects] = useState<Lazy<AspectsResponse | null>>(undefined);
  const [schema, setSchema] = useState<Lazy<SchemaDetail | null>>(undefined);
  const [contrast, setContrast] = useState<Lazy<ContrastDetail | null>>(undefined);
  const [journal, setJournal] = useState<Lazy<{ entries: JournalEntry[]; truncated: Truncation[] }>>(undefined);
  /** the journal table shows a page at a time; the count and the cap are always stated */
  const [journalAll, setJournalAll] = useState(false);
  const [journalOpen, setJournalOpen] = useState(false);
  const [openAspect, setOpenAspect] = useState<Aspect | null>(null);

  /**
   * Which category this instance is CURRENTLY showing, readable from a closure created earlier.
   * Assigned during render rather than in an effect on purpose: an effect runs after the commit,
   * and a write that resolves between the render and the effect would compare against the previous
   * value and conclude it was still on its own screen.
   */
  const idRef = React.useRef<{ sectorId: number | null; path: string }>({ sectorId: null, path: '' });
  idRef.current = { sectorId: sectorId ?? null, path };

  useEffect(() => {
    try { localStorage.setItem(LAST_PATH_KEY, path); } catch { /* convenience only */ }
  }, [path]);

  useEffect(() => {
    if (sectorId == null) return undefined;
    const ac = new AbortController();
    setLoading(true); setError(null); setD(null); setAspects(undefined); setSchema(undefined); setContrast(undefined); setJournal(undefined); setJournalAll(false); setOpenAspect(null);
    fetchCategory(sectorId, path, ac.signal)
      .then((r) => { setD(r); setLoading(false); })
      .catch((err) => { if (isAbort(err)) return; setError(err?.message || String(err)); setLoading(false); });
    return () => ac.abort();
  }, [sectorId, path]);

  // the aspects' content (existing route) — needed by Core aspects and Market data
  useEffect(() => {
    if (sectorId == null || !d || aspects !== undefined || !['aspects', 'market', 'schema'].includes(ch)) return undefined;
    const ac = new AbortController();
    fetchAspects(sectorId, path, ac.signal)
      .then((r) => setAspects({ state: 'ok', data: r }))
      .catch((err) => { if (!isAbort(err)) setAspects({ state: 'failed', error: msgOf(err) }); });
    return () => ac.abort();
  }, [sectorId, path, d, ch, aspects]);

  useEffect(() => {
    if (sectorId == null || !d || schema !== undefined || !['schema', 'contrast'].includes(ch)) return undefined;
    const ac = new AbortController();
    fetchSchema(sectorId, path, ac.signal)
      .then((r) => setSchema({ state: 'ok', data: r }))
      .catch((err) => { if (!isAbort(err)) setSchema({ state: 'failed', error: msgOf(err) }); });
    return () => ac.abort();
  }, [sectorId, path, d, ch, schema]);

  useEffect(() => {
    if (sectorId == null || !d || contrast !== undefined || ch !== 'contrast') return undefined;
    const ac = new AbortController();
    fetchContrast(sectorId, path, ac.signal)
      .then((r) => setContrast({ state: 'ok', data: r }))
      .catch((err) => { if (!isAbort(err)) setContrast({ state: 'failed', error: msgOf(err) }); });
    return () => ac.abort();
  }, [sectorId, path, d, ch, contrast]);

  // The journal of THIS category: the backend filters the source rows by exact path before it
  // groups them. Filtering the rendered text here instead dropped every write past the 14 names
  // an entry lists, and matched categories that merely share a leaf name.
  useEffect(() => {
    if (sectorId == null || !journalOpen || journal !== undefined) return undefined;
    const ac = new AbortController();
    fetchJournal(sectorId, path, ac.signal)
      .then((j) => setJournal({ state: 'ok', data: { entries: j.entries, truncated: j.truncated || [] } }))
      .catch((err) => { if (!isAbort(err)) setJournal({ state: 'failed', error: msgOf(err) }); });
    return () => ac.abort();
  }, [sectorId, path, journalOpen, journal]);

  /**
   * What happens after a write, and in this order.
   *
   * ⛔ THE CACHE FIRST, THE READ SECOND. `CategoryCockpitService` memoises the sector's reads for
   * two to ten minutes, so a refetch on its own would serve the numbers from BEFORE the write and
   * the screen would say the action had done nothing. Dropping the cache and then re-reading is
   * what makes the screen show the database rather than the memo.
   *
   * It is deliberately not `catch`-less: `<Act>` awaits this and reports a refresh that fails
   * SEPARATELY from a write that fails, because an operator who is told "the research failed" when
   * only the re-read failed will run a paid research a second time.
   *
   * ⛔⛔ AND IT COMMITS ONLY IF THE SCREEN IS STILL THE ONE IT WAS STARTED FOR. `BookBody` is NOT
   * re-mounted on navigation — the route renders one instance and swaps `path` — so a write begun
   * on A and finishing after the operator opened B would have called `setD(A)` on the instance whose
   * heading, URL and chapter requests all say B. The operator would then read A's numbers under B's
   * name, and the next action on that screen would be built from A's row. The identity is captured
   * when the write starts and compared against the live one before anything is committed; a
   * mismatch is surfaced through the refresh channel, which `<Act>` already keeps distinct from a
   * failed write. Sector switches are covered by the same comparison, not by a second mechanism.
   *
   * ⛔⛔ AND A SITE SWITCH IS NOT A MISMATCH — IT IS AN ABSENCE, WHICH THE COMPARISON CANNOT SEE.
   * `Shell` removes every child while a newly selected site's sectors resolve
   * (components/Shell.tsx:179), so this body UNMOUNTS. `idRef` then keeps the values it had, the
   * comparison above passes, and the refresh reports success for a screen that is not on the page
   * — a `setD` into nothing, reported to the operator as "done". Deregistering on unmount does not
   * reach this: the running `<Act>` captured THIS closure before the unmount and still holds it.
   * So the screen's own liveness is part of the identity it checks.
   */
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  /**
   * ⛔⛔ WHICH CHAPTERS ARE ON SCREEN, READ AT THE MOMENT THE REFRESH RUNS.
   * `afterWrite` used to clear these four to `undefined` and return. That is not a refresh: it is
   * a promise to refresh later, and the lazy effects that keep that promise fire AFTER the write
   * has already been reported as complete — and they fire ORDINARY reads, so they came back from
   * the replica and cached the stale answer for ten minutes. A rejected gap could leave the
   * summary and stay in the contrast table WITH ITS ACTION BUTTONS (both reviewers, round 11).
   * Kept in a ref rather than in `afterWrite`'s dependencies: a chapter loading must not change
   * the identity of the function a running `<Act>` is holding.
   */
  const loadedRef = useRef({ aspects: false, schema: false, contrast: false, journal: false });
  loadedRef.current = {
    aspects: aspects !== undefined, schema: schema !== undefined,
    contrast: contrast !== undefined, journal: journal !== undefined,
  };

  const afterWrite = useCallback(async () => {
    if (sectorId == null) return;
    const mine = { sectorId, path };
    /**
     * Checked after EVERY await, because there are two of them now: the header read and the
     * chapter reads. Not an error in the write and not a failure of the read — both succeeded;
     * what is refused is applying them to a screen that is gone or is showing something else.
     */
    const stillMine = () => {
      if (!alive.current) {
        throw new Error(`you left ${leaf(path)} while it ran — its screen is no longer open, so it was not refreshed and nothing here was changed; reopen it to see its new numbers`);
      }
      if (idRef.current.sectorId !== mine.sectorId || idRef.current.path !== mine.path) {
        const to = idRef.current.path ? `"${leaf(idRef.current.path)}"` : 'another screen';
        throw new Error(`you moved to ${to} while it ran, so ${leaf(path)}'s screen was not refreshed and nothing here was changed — reopen it to see its new numbers`);
      }
    };
    await dropCache(sectorId);
    // ⛔ FROM THE PRIMARY. Dropping the cache only decides that the row will be re-read; it does
    // not decide WHERE from, and the ordinary read is served by the replica and then cached for
    // ten minutes. Under lag that re-caches the pre-write row and reports the refresh as done.
    const fresh = await fetchCategory(sectorId, path, undefined, { fresh: true });
    stillMine();
    // ⛔ THE CHAPTERS ARE RE-READ HERE, FROM THE PRIMARY, AND AWAITED — not cleared and left to
    // the lazy effects, which would run after this resolves and would read the replica. Only the
    // ones actually on screen: re-reading a chapter nobody opened would spend queries to fix
    // nothing, and the lazy effect still covers it when it IS opened.
    const on = loadedRef.current;
    const [nextSchema, nextContrast, nextJournal] = await Promise.all([
      on.schema ? fetchSchema(sectorId, path, undefined, { fresh: true }) : Promise.resolve(null),
      on.contrast ? fetchContrast(sectorId, path, undefined, { fresh: true }) : Promise.resolve(null),
      on.journal ? fetchJournal(sectorId, path, undefined, { fresh: true }) : Promise.resolve(null),
    ]);
    stillMine();
    // `aspects` is served by the category-characteristics router, which has neither this cache nor
    // this replica split, so clearing it is a true refresh and not a promise of one.
    setAspects(undefined);
    setSchema(nextSchema ? { state: 'ok', data: nextSchema } : undefined);
    setContrast(nextContrast ? { state: 'ok', data: nextContrast } : undefined);
    setJournal(nextJournal ? { state: 'ok', data: { entries: nextJournal.entries, truncated: nextJournal.truncated || [] } } : undefined);
    setD(fresh);
  }, [sectorId, path]);

  const retryAspects = useCallback(() => setAspects(undefined), []);
  const retrySchema = useCallback(() => setSchema(undefined), []);
  const retryContrast = useCallback(() => setContrast(undefined), []);
  const retryJournal = useCallback(() => setJournal(undefined), []);

  const now = Date.now();
  const x = useMemo(
    () => (d ? { d, aspects: dataOf(aspects), schema: dataOf(schema), contrast: dataOf(contrast), openAspect, setOpenAspect, now, siteId } : null),
    [d, aspects, schema, contrast, openAspect, now, siteId],
  );

  /**
   * ⛔⛔ THE RESULT STORE SITS ABOVE THESE BRANCHES, NOT UNDER THEM.
   * `<ActProvider>` used to wrap only the loaded page, so opening another category — which sets
   * `loading` and clears `d` — unmounted the provider that OWNS the log, and every finished write's
   * result went with it. That is the same loss rule 2 of `<Act>` exists to prevent, one level out:
   * there it was the chapter that re-mounted, here it is the whole page. It also silenced the
   * navigation guard below, whose entire output is a message rendered into that log.
   * ⇒ The page's three states are rendered as a VALUE, and the provider is not here at all any
   *   more: it is mounted above `<Shell>`, because Shell itself drops its children on a SITE
   *   switch (components/Shell.tsx:179) and would have taken a provider mounted here with it.
   *   This body REGISTERS its refresh with that provider and deregisters when it goes away.
   */
  const reach = d
    ? `${reachSentence(d.category.blast.sector, 'knowledge', d.sector.name)} (the knowledge); ${reachSentence(d.category.blast.global, 'schema', d.sector.name)} (the schema)`
    : undefined;

  useProvideAct(
    { afterWrite, reach },
    `you left ${leaf(path)} while it ran, so its screen was not refreshed and nothing here was changed — reopen it to see its new numbers`,
  );

  return <>{renderPage()}</>;

  function renderPage(): React.ReactNode {
  if (loading) return <Loading label={`Reading ${leaf(path)}…`} />;
  if (error) return <ErrorBox title="The category could not be loaded" msg={error} />;
  if (!d || !x) return null;

  const c = d.category;
  const sites = sitesOf(c.sites);
  const prods = sum(sites, (s) => s.products); const enr = sum(sites, (s) => s.enriched); const pages = sum(sites, (s) => s.pages_published);
  const scored = sites.filter((s) => s.tier);
  const a = c.derived.next_action;
  const parts = path.split(' > ');
  const st = c.derived.chapters[ch];
  const brief = chapterBrief(ch, x);
  const n = CHAPTERS.indexOf(ch) + 1;
  const bl = c.blast;
  const actionTo = a.ch === 'inbox' ? '/category-cockpit/decide' : catUrl(path, a.ch);

  // A chapter that depends on a request which FAILED shows the failure, not a reading of data
  // it never received. Order follows the load order, so the first thing that broke is named.
  const journalError = errorOf(journal);
  let blocked: { what: string; msg: string; retry: () => void } | null = null;
  if (['aspects', 'market', 'schema'].includes(ch) && errorOf(aspects)) {
    blocked = { what: 'The researched aspects of this category', msg: errorOf(aspects) as string, retry: retryAspects };
  } else if (['schema', 'contrast'].includes(ch) && errorOf(schema)) {
    blocked = { what: 'The schema of this category', msg: errorOf(schema) as string, retry: retrySchema };
  } else if (ch === 'contrast' && errorOf(contrast)) {
    blocked = { what: 'The pending gaps of this category', msg: errorOf(contrast) as string, retry: retryContrast };
  }

  return (
    <>
      <div className="pmk-cat-h">
        <div>
          <div className="pmk-crumb"><Link to="/category-cockpit">{d.sector.name}</Link>{parts.slice(0, -1).map((p, i) => <React.Fragment key={p + i}> › <Link to={catUrl(parts.slice(0, i + 1).join(' > '))}>{p}</Link></React.Fragment>)}</div>
          <h1 className="pmk-h1">{leaf(path)}</h1>
          <div className="pmk-facts">
            <span><b>{plural(sites.length, 'site')}</b>{c.sectors_with_this_path > 1 ? <> · also under <b>{c.sectors_with_this_path - 1} other sector{c.sectors_with_this_path > 2 ? 's' : ''}</b></> : null}</span>
            <span><b>{fmt(prods)}</b> products{enr ? <>, <b>{pct(enr, prods)}</b> enriched</> : null}</span>
            <span><b>{fmt(pages)}</b> pages published</span>
            <span>{scored.length ? scored.map((s) => <React.Fragment key={s.site_id}><TierPill site={s} /> <b>{s.is_ready ? 'ready' : 'not ready'}</b> <span className="pmc-mute">{s.site_name}</span> </React.Fragment>) : <span className="pmc-mute">never scored</span>}</span>
            <span>research <AspectDots aspects={c.derived.aspects} /> {c.last_researched_at ? <b>{day(c.last_researched_at)}</b> : <b>never</b>}</span>
            <span data-tip="category_characteristics.times_used / last_used_at">read by enrichment <b>{fmt(c.times_used || 0)}</b> times{c.last_used_at ? <>, last {day(c.last_used_at)}</> : null}</span>
            <span>ready <Dots dots={c.derived.dots} /></span>
          </div>
          <div className="pmk-reach">
            {/* the SAME sentences every write confirmation shows — one function, so the header and
                the confirmation cannot disagree about how far a change travels */}
            <span data-tip={`category_characteristics is keyed (sector_id, category_path): a change to the 10 aspects reaches the sites of ${d.sector.name} that carry this path`}><b>A change to the knowledge reaches</b> {reachSentence(bl.sector, 'knowledge', d.sector.name)}.</span>
            <span className={bl.global.others.length ? 'warn' : ''} data-tip="canonical_attributes is keyed by path only: a schema change reaches every sector carrying the path"><b>A change to the schema reaches</b> {reachSentence(bl.global, 'schema', d.sector.name)}.</span>
          </div>
        </div>
        <div className="pmk-side">
          <StatePill k={a.k} title={a.why}>{a.t}</StatePill>
          {!a.none && <Link className="pmc-btn primary" to={actionTo}>{a.ch === 'inbox' ? 'Open To decide' : `Open ${CH_TITLES[a.ch as ChapterId]}`}</Link>}
          <span className="pmc-small pmc-mute">{a.why}</span>
        </div>
      </div>

      <div className="pmk-catgrid">
        <nav className="pmk-rail" aria-label="Chapters">
          {CHAPTERS.map((k, i) => {
            const s = c.derived.chapters[k].status;
            return (
              <Link key={k} to={catUrl(path, k)} className={k === ch ? 'on' : ''} aria-current={k === ch ? 'page' : undefined} onClick={(e) => { e.preventDefault(); navigate(catUrl(path, k)); }}>
                <span className="n">{String(i + 1).padStart(2, '0')}</span><span>{CH_TITLES[k]}</span><span className={`st ${s[1]}`} data-tip={`${s[0]}${s[2] ? ` · ${s[2]}` : ''}`} />
              </Link>
            );
          })}
          <div className="lg">One dot per chapter: <StatePill k="ready" small>live</StatePill> <StatePill k="attention" small>stale</StatePill> <StatePill k="todo" small>empty</StatePill> <StatePill k="off" small>off</StatePill></div>
        </nav>
        <section className="pmk-chap" id={`ch-${ch}`}>
          <div className="pmk-chap-hd">
            <span className="pmc-small pmc-mute">{String(n).padStart(2, '0')}</span>
            <h2>{CH_TITLES[ch]}</h2>
            <StatePill k={STATE_MAP[st.status[1]] || 'todo'} title={st.status[2]}>{st.status[0]}</StatePill>
            <Ladder l={st.ladder} />
          </div>
          {blocked ? <LoadFailed what={blocked.what} msg={blocked.msg} retry={blocked.retry} /> : (
            <>
              <dl className="pmk-brief">
                <dt>What we know</dt><dd>{brief.know || '—'}</dd>
                <dt>What is missing</dt><dd>{brief.miss || '—'}</dd>
                {brief.act && <><dt>Action</dt><dd className="act">{brief.act}</dd></>}
              </dl>
              <div className="pmk-body"><ChapterBody id={ch} x={x} /></div>
            </>
          )}
        </section>
      </div>

      <details className="pmk-catjr" open={journalOpen} onToggle={(e) => setJournalOpen((e.currentTarget as HTMLDetailsElement).open)}>
        <summary>Journal of {leaf(path)} <span className="pmc-mute">{journal && journal.state === 'ok' ? plural(journal.data.entries.length, 'entry').replace('entrys', 'entries') : journal ? 'could not be read' : journalOpen ? 'loading…' : 'timeline and writes recorded for this category'}</span><Link to="/category-cockpit/journal" onClick={(e) => e.stopPropagation()}>all writes →</Link></summary>
        {journalOpen && (() => {
          // the "timeline as derivable today" of the prototype: what the tables already give without a version store
          const ser = d.readiness_series;
          const ev: Array<[string | null, string, string]> = [
            [c.created_at, 'row created', 'category_characteristics.created_at'],
            ...(c.last_researched_at ? [[c.last_researched_at, `research run · ${c.research_model || '?'}${c.sample_product_count ? ` · grounded on ${c.sample_product_count} products` : ''} · ${cost(c.research_cost_usd)}`, 'category_characteristics.last_researched_at'] as [string, string, string]] : []),
            ...(d.research_history || []).slice(0, 12).map((h) => [h.started_at, `${h.success === false ? 'failed ' : ''}research of ${h.research_type} · ${h.model || '?'}${h.sample_product_count ? ` · ${h.sample_product_count} samples` : ''}`, 'category_characteristics_research_history'] as [string | null, string, string]),
            ...(ser.length ? [[ser[0].day, `first readiness snapshot · ${ser[0].tier} ${Number(ser[0].overall_score).toFixed(2)}`, 'category_readiness_score_history'] as [string, string, string], [ser[ser.length - 1].day, `latest readiness snapshot · ${ser[ser.length - 1].tier} ${Number(ser[ser.length - 1].overall_score).toFixed(2)} · ${ser.length} daily snapshots`, 'category_readiness_score_history'] as [string, string, string]] : []),
            ...d.knowledge_notes.filter((n) => n.scope_level === 'category').slice(0, 3).map((n) => [n.created, `knowledge note · ${n.title} · ${n.status}`, 'domain_knowledge'] as [string, string, string]),
            [c.last_used_at, `last read by enrichment · ${fmt(c.times_used || 0)} reads in total`, 'category_characteristics.last_used_at / times_used'],
            [c.stale_after, 'becomes stale', 'category_characteristics.stale_after'],
          ].filter((e) => e[0]).sort((a, b) => String(a[0]).localeCompare(String(b[0]))) as Array<[string, string, string]>;
          return (
            <div className="pmk-timeline">
              <div className="pmk-up">Timeline as derivable today — what the tables already give without a version store</div>
              {ev.map((e, i) => <div key={i} className="pmk-ev"><span className="d">{day(e[0])}</span><span>{e[1]}<span className="x">{e[2]}</span></span></div>)}
              <div className="pmc-small pmc-mute">No diff, no author, no rollback: the history table keeps one column's previous value per write and nothing reads it. A version history (snapshot, diff, roll back) is the next band of the plan.</div>
            </div>
          );
        })()}
        {journalError && <LoadFailed what={`The journal of ${leaf(path)}`} msg={journalError} retry={retryJournal} />}
        {journal && journal.state === 'ok' && (journal.data.entries.length ? (() => {
          const all = journal.data.entries;
          const page = journalAll ? all : all.slice(0, JOURNAL_PAGE);
          return (
            <>
              {!!journal.data.truncated.length && (
                <div className="pmk-note warn">
                  <b>Bounded history.</b>{' '}
                  {journal.data.truncated.map((t) => `${t.source.replace(/_/g, ' ')} stops at ${t.limit} groups`).join(' · ')}.
                  Older writes of those kinds are not below.
                </div>
              )}
              <table className="pmk-jr">
                <tbody>{page.map((e, i) => <tr key={i}><td className="when">{String(e.at).slice(0, 10)}</td><td><span className={`pmk-kind ${e.kind}`}>{e.kind}</span></td><td className="what">{e.what}<span className="dt">{e.who}{e.how ? ` · ${e.how}` : ''}</span></td><td className="tbl">{e.tbl}</td></tr>)}</tbody>
              </table>
              {all.length > JOURNAL_PAGE && (
                <div className="pmk-jr-more">
                  <span className="pmc-small pmc-mute">Showing {page.length} of {all.length} entries.</span>
                  <button type="button" className="pmc-btn" onClick={() => setJournalAll(!journalAll)}>
                    {journalAll ? `Show the first ${JOURNAL_PAGE}` : `Show all ${all.length}`}
                  </button>
                </div>
              )}
            </>
          );
        })() : <div className="pmc-empty"><b>No write recorded for this category.</b> The journal is derived from the timestamps the tables keep.</div>)}
      </details>
      <Src>computed {d.computed_at.slice(0, 16).replace('T', ' ')} UTC · {d._meta.source} · {d._meta.computed_ms} ms{d._meta.list_cached ? ' · list cached' : ''}</Src>
    </>
  );
  }
}
